import React, { useEffect, useRef, useState } from 'react';
import { Comment, CommentReply } from '../../types';
import {
  getReplies,
  addReply,
  resolveComment,
  getCommentActionErrorMessage,
} from '../../services/comments';
import { getUserLabel } from '../../services/users';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, MessageSquarePlus } from 'lucide-react';

interface CommentsPanelProps {
  projectId: string;
  comments: Comment[];
  loading?: boolean;
  canReply?: boolean;
  selectedCommentId?: string | null;
  onSelectComment?: (commentId: string) => void;
  canResolve?: boolean;
  onClose: () => void;
}

export function CommentsPanel({
  projectId,
  comments,
  loading,
  canReply = false,
  selectedCommentId,
  onSelectComment,
  canResolve = false,
  onClose,
}: CommentsPanelProps) {
  const { currentUser } = useAuth();
  const [repliesByComment, setRepliesByComment] = useState<Record<string, CommentReply[]>>({});
  const [loadingReplies, setLoadingReplies] = useState<Record<string, boolean>>({});
  const [replyInput, setReplyInput] = useState<Record<string, string>>({});
  const commentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [authorLabels, setAuthorLabels] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  async function loadReplies(commentId: string) {
    if (loadingReplies[commentId]) return;
    setActionError(null);
    setLoadingReplies((prev) => ({ ...prev, [commentId]: true }));
    try {
      const replies = await getReplies(projectId, commentId);
      setRepliesByComment((prev) => ({ ...prev, [commentId]: replies }));
    } catch (err) {
      setActionError(
        getCommentActionErrorMessage(err, 'Failed to load replies. Please try again.')
      );
    } finally {
      setLoadingReplies((prev) => ({ ...prev, [commentId]: false }));
    }
  }

  async function handleAddReply(commentId: string) {
    if (!currentUser || !canReply) return;
    const text = (replyInput[commentId] || '').trim();
    if (!text) return;
    setActionError(null);
    try {
      await addReply(projectId, commentId, {
        authorId: currentUser.uid,
        text,
      });
      setReplyInput((prev) => ({ ...prev, [commentId]: '' }));
      await loadReplies(commentId);
    } catch (err) {
      setActionError(
        getCommentActionErrorMessage(err, 'Failed to add your reply. Please try again.')
      );
    }
  }

  async function handleResolve(commentId: string) {
    if (!currentUser || !canResolve) return;
    setActionError(null);
    try {
      await resolveComment(projectId, commentId, currentUser.uid);
    } catch (err) {
      setActionError(
        getCommentActionErrorMessage(err, 'Failed to resolve this comment. Please try again.')
      );
    }
  }

  useEffect(() => {
    const ids = new Set<string>();
    comments.forEach((comment) => ids.add(comment.authorId));
    Object.values(repliesByComment).forEach((replies) => {
      replies.forEach((reply) => ids.add(reply.authorId));
    });
    const missing = Array.from(ids).filter((id) => !authorLabels[id]);
    if (missing.length === 0) return;
    missing.forEach((id) => {
      getUserLabel(id).then((label) => {
        setAuthorLabels((prev) => ({ ...prev, [id]: label }));
      });
    });
  }, [comments, repliesByComment, authorLabels]);

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
        <div>
          <h2 className="font-semibold text-gray-900">Comments</h2>
          <p className="text-sm text-gray-500">
            {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
          </p>
        </div>
        <button
          onClick={onClose}
          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
        >
          Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {actionError && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {actionError}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            <MessageSquarePlus className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            No comments yet
          </div>
        ) : (
          <div className="space-y-2 w-1/2 min-w-[260px] max-w-sm">
            {comments.map((comment) => {
              const replies = repliesByComment[comment.id] || [];
              const isLoadingReplies = loadingReplies[comment.id];
              return (
                <div
                  key={comment.id}
                  ref={(el) => {
                    commentRefs.current[comment.id] = el;
                  }}
                  onClick={() => onSelectComment?.(comment.id)}
                  className={`border rounded-lg p-2 cursor-pointer ${
                    selectedCommentId === comment.id
                      ? 'border-blue-300 bg-blue-50/50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between text-[11px] text-gray-400">
                    <span>
                      Lines {comment.selection.startLine}-{comment.selection.endLine}
                    </span>
                    <span>{comment.createdAt.toLocaleDateString()}</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    {authorLabels[comment.authorId] || 'Unknown'}
                  </div>
                  <div className="text-sm text-gray-800 mt-1">
                    {comment.text}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1 italic">
                    “{comment.snippet}”
                  </div>

                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void loadReplies(comment.id);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {replies.length > 0 ? `View replies (${replies.length})` : 'View replies'}
                    </button>
                    {isLoadingReplies && (
                      <Loader2 className="inline-block h-3 w-3 ml-2 animate-spin text-gray-400" />
                    )}
                  </div>

                  {replies.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {replies.map((reply) => (
                        <div key={reply.id} className="text-xs text-gray-700 border-l-2 border-gray-200 pl-2">
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            {authorLabels[reply.authorId] || 'Unknown'}
                          </div>
                          {reply.text}
                        </div>
                      ))}
                    </div>
                  )}

                  {canReply && (
                    <div className="mt-2">
                      <input
                        type="text"
                        placeholder="Reply..."
                        value={replyInput[comment.id] || ''}
                        onChange={(e) =>
                          setReplyInput((prev) => ({ ...prev, [comment.id]: e.target.value }))
                        }
                        className="w-full text-sm px-3 py-2 border border-gray-200 rounded"
                      />
                      <div className="flex justify-end mt-2 gap-2">
                        {canResolve && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleResolve(comment.id);
                            }}
                            className="px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                          >
                            Resolve
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleAddReply(comment.id);
                          }}
                          disabled={!replyInput[comment.id]?.trim()}
                          className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                        >
                          Reply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
